import { type PropsWithChildren, useEffect, useMemo } from 'react';
import { assertDefined } from '@epihub/core/src/fn/assert';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import type { UseGlobalStoreWithSlice } from '@epihub/web/src/zustand/types';
import { ErrorBoundary, type ErrorBoundaryProps } from '@epihub/web/src/components/ErrorBoundary';
import { styled } from '@epihub/web/src/_ctx/theme';
import { Screen } from '@epihub/web/src/dimensions';
import { expectWebDeps } from '@epihub/web/src/_ctx/deps';
import type { ThemeStoreSlice } from '../zustand';
import { themeStateVersioner, themeVersioner } from '../types';
import type { EmotionThemeType } from '../emotion';
import { createWebSnackbarProvider } from './createWebSnackbarProvider';

type ProviderProps = {
  useGlobalStore: UseGlobalStoreWithSlice<ThemeStoreSlice>;
  errorBoundaryProps: ErrorBoundaryProps;
};

export function createWebProvider() {
  const MantineProvider = assertDefined(
    expectWebDeps().mantine?.MantineProvider,
    '@mantine/core import was not passed as an injectable dependency',
  );

  const MantineSnackbarProvider = createWebSnackbarProvider();

  return ({ children, useGlobalStore, errorBoundaryProps }: PropsWithChildren<ProviderProps>) => {
    const theme = useGlobalStore((store) => store.theme.theme);
    const state = useGlobalStore((store) => store.theme.__state);
    const updateTheme = useGlobalStore((store) => store.theme.updateTheme);

    useEffect(() => {
      const listener = () => {
        updateTheme((themeState, getTheme) => {
          // @ts-expect-error
          themeState.screen = new Screen(window.innerWidth, window.innerHeight);
        });
      };

      window.addEventListener('resize', listener);
      return () => {
        window.removeEventListener('resize', listener);
      };
    }, []);

    const versionedTheme = useMemo(
      () => {
        return {
          ...theme,
          v0: themeVersioner('v0', theme),
          updateTheme: updateTheme as EmotionThemeType['updateTheme'],
        } satisfies EmotionThemeType;
      },
      [theme, updateTheme],
    );

    const stateV0 = useMemo(
      () => themeStateVersioner('v0', state),
      [state],
    );

    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <EmotionThemeProvider theme={versionedTheme}>
          <TailwindThemed>
            <MantineProvider
              forceColorScheme={stateV0.scheme}
              theme={versionedTheme.v0.mantine}
            >
              <MantineOverrides id='mantine-overrides'>
                <MantineSnackbarProvider>
                  {children}
                </MantineSnackbarProvider>
              </MantineOverrides>
            </MantineProvider>
          </TailwindThemed>
        </EmotionThemeProvider>
      </ErrorBoundary>
    );
  };
}

const MantineOverrides = styled.div`
  a {
    text-decoration: underline;
    color: var(--mantine-color-anchor);
  }
`;

const TailwindThemed = styled.div`
  --font-display: ${({ theme }) => theme.v0.font.family.base.map((s) => `"${s}"`).join(', ')};
  --color-primary-500: ${({ theme }) => theme.v0.col.primary.hex};
  --spacing: 0.25rem;
`;
